import { useReducer, createContext, useEffect, useContext, useRef } from 'react'
import { initializeApp } from 'firebase/app'
import {
    getAuth,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
} from 'firebase/auth'
import { osmosisFirebaseConfig } from '../config/osmosisConfig'
import { useToastMessage } from './ToastPopUpContext'
import { getAuthentication } from '../apiRoutes/authenticationRoutes'
import { useSocket } from './SocketContext'
import { editUser } from '../apiRoutes'
import { useGlobalState } from './GlobalStateContext'

import { Grid } from '../components/UniversalComponents'
import { CircularProgress, Typography } from '@mui/material'

interface ContextValues {
    auth?: any
    setAuth?: (newAuth) => {}
    signIn?: ({ auth, username, password }) => {}
    signOutApp?: (param?: any) => {}
}

interface Preferences {
    boatManagementTable: any[]
}

type Auth = {
    user?: any
    authenticated?: boolean
    preferences?: Preferences
}

type Props = {
    children: React.ReactNode
}

const app = initializeApp(osmosisFirebaseConfig)
export const authConnection = getAuth(app)

export const Authentication = createContext({
    auth: {
        userPreferences: {
            boatManagementTable: [],
            dataTableColumns: {
                devices: {
                    columns: {},
                    density: '',
                },
                engines: {
                    engine: {
                        columns: {},
                        density: '',
                    },
                    engineCalibration: {
                        columns: {},
                        density: '',
                    },
                    engineModel: {
                        columns: {},
                        density: '',
                    },
                    engineManufacturers: {
                        columns: {},
                        density: '',
                    },
                },
                boats: {
                    boatConfig: {
                        columns: {},
                        density: '',
                    },
                    boatModels: {
                        columns: {},
                        density: '',
                    },
                    boatManufacturers: {
                        columns: {},
                        density: '',
                    },
                    boatProvisioning: {
                        columns: {},
                        density: '',
                    },
                    unpairedDeviceBoats: {
                        columns: {},
                        density: '',
                    },
                },
                dealers: {
                    columns: {},
                    density: '',
                },
                signals: {
                    signalManager: {
                        columns: {},
                        density: '',
                    },
                    signalEvents: {
                        columns: {},
                        density: '',
                    },
                    unitOfMeasurement: {
                        columns: {},
                        density: '',
                    },
                    signalProfileManager: {
                        columns: {},
                        density: '',
                    },
                    signalFormulaManager: {
                        columns: {},
                        density: '',
                    },
                },
                system: {
                    osPatchNotes: {
                        columns: {},
                        density: '',
                    },
                    mobilePatchNotes: {
                        columns: {},
                        density: '',
                    },
                },
                users: {
                    userAdmin: {
                        columns: {},
                        density: '',
                    },
                    userNotificationSubscription: {
                        columns: {},
                        density: '',
                    },
                    userRegistration: {
                        columns: {},
                        density: '',
                    },
                },
                supportTicketAdmin: {
                    columns: {},
                    density: '',
                },
                manufacturers: {
                    columns: {},
                    density: '',
                },
            },
        },
        userRole: '',
        userPermissions: {},
        _id: '',
        username: '',
        activeManufacturer: { manufacturerName: '' },
        manufacturer: [],
        authenticated: false,
        loading: true,
        idToken: null,
        user: { activeManufacturer: { _id: '' }, assignedDealer: '' },
        userProfile: {
            superAdmin: false,
            userProfilePermissions: {
                Users: {
                    read: false,
                    write: false,
                },
            },
            dealerProfile: false,
            userProfileAssignedManufacturers: [],
        },
        signingIn: false,
    },

    setAuth: (param) => {},
    signIn: (param) => {},
    signOutApp: (param?: any) => {},
    setUser: (param: any) => {},
})
export const AuthenticationContext = ({ children }: Props) => {
    const setToastMessage = useToastMessage()
    const { apiSocket } = useSocket()

    const { setGlobalState } = useGlobalState()

    let defaultState = {
        userPreferences: {},
        userRole: '',
        userPermissions: {},
        activeManufacturer: { manufacturerName: '' },
        manufacturer: [],
        _id: '',
        username: '',
        authenticated: false,
        loading: true,
        idToken: null,
        user: {},
        userProfile: { superAdmin: false },
        signingIn: false,
    }
    const reducer = (oldState: any, newState: any) => ({
        ...oldState,
        ...newState,
    })
    const [state, setState] = useReducer(reducer, defaultState)
    const { authenticated, _id, activeManufacturer } = state

    const setAuth = (newAuth) => {
        setState({ ...newAuth })
    }

    const signIn = async ({ username, password }) => {
        try {
            if (authConnection) {
                if (username && password) {
                    const authenticate = await signInWithEmailAndPassword(
                        authConnection,
                        username,
                        password
                    )

                    if (authenticate) {
                        const { user } = authenticate
                        const { uid } = user
                        if (uid) {
                            const currentSession = await getAuthentication({
                                uid,
                            })
                            if (currentSession) {
                                const idToken = await user.getIdToken()
                                globalThis.username = currentSession.username
                                setAuth({
                                    ...currentSession,
                                    authenticated: true,
                                    loading: false,
                                    idToken,
                                    signingIn: true,
                                })
                            }
                        }
                    }
                }
            }
        } catch (err: any) {
            setAuth({ ...state, loading: false })
            setToastMessage('Invalid Username or Password', 'error')
            console.error(err)
        }
    }
    const signOutApp = async () => {
        try {
            await signOut(authConnection)
            setGlobalState({ defaultState: {} })
            setAuth({ ...defaultState, loading: false })
        } catch (err) {
            console.error('Sign-out error:', err)
            setAuth({ ...defaultState, loading: false })
        }
    }

    interface SetUserParams {
        key: string
        value: any
        type?: string
    }

    const setUser = async ({ key, value, type }: SetUserParams) => {
        try {
            let parsedFormData = {}
            if (!type) {
                parsedFormData = { id: _id, ...state.user, [key]: value }
            } else if (type === 'columnData') {
                parsedFormData = {
                    id: _id,
                    ...state.user,
                    userPreferences: {
                        ...state.user.userPreferences,
                        dataTableColumns: {
                            ...state.user.userPreferences?.dataTableColumns,
                            [key]: {
                                ...state.user.userPreferences
                                    .dataTableColumns?.[key],
                                ...value,
                            },
                        },
                    },
                }
            }

            console.log(parsedFormData, 'formData')
            let res: any

            res = await editUser(parsedFormData)
            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    setToastMessage(
                        `User: ${data.userEmail} preferences were successfully edited`,
                        'success'
                    )
                    // getData()
                } else {
                    throw new Error('User edit was unsuccessfull')
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err.message}`, 'error')
        }
    }

    const getData = async () => {
        try {
            const currentSession = await getAuthentication({
                _id,
            })

            if (currentSession) {
                setAuth({
                    ...currentSession,
                    authenticated: true,
                })
            } else {
                throw new Error('No user found')
            }
        } catch (error) {
            console.error('error')
        }
    }

    useEffect(() => {
        const getLocalAuth = async () => {
            if (authConnection) {
                onAuthStateChanged(authConnection, async (user) => {
                    if (user) {
                        const { uid } = user
                        if (uid) {
                            const currentSession = await getAuthentication({
                                uid,
                            })
                            if (currentSession) {
                                const idToken = await user.getIdToken()
                                globalThis.username = currentSession.username
                                setAuth({
                                    ...currentSession,
                                    authenticated: true,
                                    loading: false,
                                    idToken,
                                })
                            }
                        }
                    } else {
                        globalThis.username = ''
                        setAuth({
                            ...defaultState,
                            authenticated: false,
                            loading: false,
                        })
                    }
                })
            }
        }
        getLocalAuth()
    }, [])

    // useEffect(() => {
    //     if (authenticated && _id) {
    //         apiSocket?.on(_id, () => {
    //             getData()
    //         })
    //     }
    // }, [authenticated, _id])

    return (
        <Authentication.Provider
            value={{
                auth: state,
                setAuth,
                signIn,
                signOutApp,
                setUser,
            }}
        >
            {children}
        </Authentication.Provider>
    )
}

export const useAuth = () => useContext(Authentication)
export const usePermissions = () => {
    const currentUser = useContext(Authentication)
    const { userPermissions, userRole, activeManufacturer, userPreferences } =
        currentUser.auth
    return {
        ...userPermissions,
        userRole,
        activeManufacturer,
        ...userPreferences,
    }
}
